import { loadConfig, loadOperatorSession, clearOperatorSession } from './config.js';
import type {
  AsnDashboardSummary,
  SessionWithItems,
  ScanOutcome,
  UndoOutcome,
  ManualEditOutcome,
  ReviewLine,
  ConfirmOutcome,
  PartialPushOutcome,
  ReceivingSession,
  ReceivingException,
  CorrectionReason,
  CurrentUser,
  OperatorSummary,
  LoginResult,
  RecordStockCountOutcome,
  ExpectedQuantity,
  ExpectedSkuTotal,
  StockLookupResult,
  ApplyStockCountResult,
  StockCount,
  PickBatchSummary,
  BatchPickView,
  PickConfirmResult,
  PickStock,
  ReplenList,
  ReleaseList,
  ReplenMoveResult,
} from './types.js';

/**
 * Thrown for a non-2xx response the backend explains with {error, code?}
 * (apps/worker/src/index.ts's mapDomainError shape). Never thrown for a
 * network failure — see ApiNetworkError below, which callers (the scan
 * screen especially) need to tell apart from a real domain error so they
 * know whether to queue-and-retry (offlineQueue.ts) or show the error
 * to the operator immediately.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Fetch itself failed (offline, DNS, CORS, server unreachable) — distinct from ApiError, which means the server answered and said no. */
export class ApiNetworkError extends Error {
  constructor(cause: unknown) {
    super(`Network request failed: ${String(cause)}`);
    this.name = 'ApiNetworkError';
  }
}

/**
 * `skipAuth: true` is for the two endpoints callable before an operator
 * is logged in (listing operators for the Login screen's picker, and
 * logging in itself) — they must NOT send a stale/absent X-Operator-Token,
 * only the device-level X-App-Token (already sent unconditionally below).
 */
async function request<T>(path: string, init?: RequestInit, opts?: { skipAuth?: boolean }): Promise<T> {
  const config = loadConfig();
  if (!config.serverUrl) {
    throw new ApiError('No server configured — open Settings and enter the Worker URL.', 0);
  }
  const headers = new Headers(init?.headers);
  headers.set('X-App-Token', config.appToken);
  if (!opts?.skipAuth) {
    const session = loadOperatorSession();
    if (session) headers.set('X-Operator-Token', session.token);
  }
  if (init?.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');

  let response: Response;
  try {
    response = await fetch(`${config.serverUrl}${path}`, { ...init, headers });
  } catch (err) {
    throw new ApiNetworkError(err);
  }

  const text = await response.text();
  const data = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const body = (data ?? {}) as { error?: string; code?: string };
    if (response.status === 401 && !opts?.skipAuth) {
      // The operator's session is missing/expired/revoked (or the
      // operator was deactivated after logging in) — drop it locally too,
      // so the next navigation sends them to Login instead of repeating
      // the same failing request. main.ts listens for this to redirect
      // immediately rather than waiting for the operator to notice.
      clearOperatorSession();
      window.dispatchEvent(new CustomEvent('wr:session-expired'));
    }
    throw new ApiError(body.error ?? `Request failed (${response.status})`, response.status, body.code);
  }
  return data as T;
}

export const api = {
  getAsns: () => request<AsnDashboardSummary[]>('/api/asns'),

  startOrResumeReceiving: (asnId: string, deviceId?: string) =>
    request<ReceivingSession>(`/api/asns/${encodeURIComponent(asnId)}/receiving`, {
      method: 'POST',
      body: JSON.stringify({ deviceId }),
    }),

  getSession: (sessionId: string) => request<SessionWithItems>(`/api/receiving/${encodeURIComponent(sessionId)}`),

  scan: (sessionId: string, input: { ean: string; deviceId?: string; correlationId: string }) =>
    request<ScanOutcome>(`/api/receiving/${encodeURIComponent(sessionId)}/scans`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  recordException: (
    sessionId: string,
    input: { ean: string | null; sku: string | null; type: 'UNKNOWN_BARCODE' | 'UNEXPECTED_PRODUCT'; notes?: string },
  ) =>
    request<ReceivingException>(`/api/receiving/${encodeURIComponent(sessionId)}/exceptions`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  undo: (sessionId: string, input: { itemId: string; deviceId?: string }) =>
    request<UndoOutcome>(`/api/receiving/${encodeURIComponent(sessionId)}/undo`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  setQuantity: (
    sessionId: string,
    itemId: string,
    input: {
      newQuantity: number;
      correctionReason?: CorrectionReason;
      correctionNotes?: string;
      confirmedLargeChange?: boolean;
      deviceId?: string;
    },
  ) =>
    request<ManualEditOutcome>(`/api/receiving/${encodeURIComponent(sessionId)}/items/${encodeURIComponent(itemId)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),

  review: (sessionId: string) => request<ReviewLine[]>(`/api/receiving/${encodeURIComponent(sessionId)}/review`),

  confirm: (sessionId: string) =>
    request<ConfirmOutcome>(`/api/receiving/${encodeURIComponent(sessionId)}/confirm`, { method: 'POST' }),

  /**
   * Added 2026-10-01 — the instalment-delivery "Confirm this delivery"
   * action (claude/phase1-partial-receiving-investigation.md). Pushes
   * whatever's been scanned so far to Mintsoft (sellable immediately)
   * WITHOUT locking the session, so scanning can continue on a later day.
   * Deliberately a separate method from confirm() above, not a parameter
   * to it — same Supervisor/Admin gate, but a distinct, lower-stakes
   * action that must never be confusable with the final, irreversible
   * Confirm Receiving.
   */
  confirmPartial: (sessionId: string) =>
    request<PartialPushOutcome>(`/api/receiving/${encodeURIComponent(sessionId)}/confirm-partial`, { method: 'POST' }),

  health: () => request<{ ok: boolean }>('/api/health'),

  /** Added 2026-09-30 alongside the Supervisor-only Confirm restriction — lets a screen check the current device's operator's own role before showing/hiding gated actions. */
  me: () => request<CurrentUser>('/api/me'),

  /** The Login screen's name picker — active operators only, pre-login (no operator token sent or required). */
  listOperators: () => request<OperatorSummary[]>('/api/auth/operators', undefined, { skipAuth: true }),

  /** PIN login (replaces the old free-text Operator ID). Pre-login — no operator token sent. */
  login: (input: { operatorId: string; pin: string; deviceId?: string }) =>
    request<LoginResult>('/api/auth/login', { method: 'POST', body: JSON.stringify(input) }, { skipAuth: true }),

  /** Revokes the current device's session token server-side. Sends the token (not skipAuth) so there's something for the server to actually revoke. */
  logout: () => request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),

  /**
   * Stock Count (Suri, 2026-10-05). One call per physical count: the
   * backend resolves the barcode/SKU against Mintsoft itself, reads its
   * current inventory, and records the count — superseding any
   * not-yet-applied earlier count of the same SKU ("latest scan wins").
   * Either `ean` or `sku` is required; the scan screen always sends
   * `ean` for a scanned barcode and falls back to `sku` for manual entry.
   * `location` (added 2026-10-06 — counts are per location) is the scanned/typed
   * location label; the backend resolves it against Mintsoft and answers 404
   * with code LOCATION_NOT_FOUND if there's no such location.
   */
  recordStockCount: (input: { ean?: string; sku?: string; productId?: number; location: string; countedQuantity: number }) =>
    request<RecordStockCountOutcome>('/api/stock-counts', { method: 'POST', body: JSON.stringify(input) }),

  /** The admin report's data source — defaults to PENDING (what's awaiting apply). */
  /** Read-only: what Mintsoft expects for this SKU in this location (shown before the count is typed). */
  expectedStockCount: (input: { ean?: string; sku?: string; productId?: number; location: string }) => {
    const q = new URLSearchParams({ location: input.location });
    if (input.productId) q.set('productId', String(input.productId));
    else if (input.ean) q.set('ean', input.ean);
    else if (input.sku) q.set('sku', input.sku);
    return request<ExpectedQuantity>(`/api/stock-counts/expected?${q.toString()}`);
  },

  /** SKU count (2026-10-16): record the TOTAL counted for one SKU, all locations. */
  recordSkuCount: (input: { ean?: string; sku?: string; productId?: number; countedQuantity: number }) =>
    request<RecordStockCountOutcome>('/api/stock-counts/sku', { method: 'POST', body: JSON.stringify(input) }),
  /** Read-only: the total Mintsoft expects for a SKU and where it is. */
  expectedSkuCount: (input: { ean?: string; sku?: string; productId?: number }) => {
    const q = new URLSearchParams();
    if (input.productId) q.set('productId', String(input.productId));
    else if (input.ean) q.set('ean', input.ean);
    else if (input.sku) q.set('sku', input.sku);
    return request<ExpectedSkuTotal>(`/api/stock-counts/expected-sku?${q.toString()}`);
  },

  /** Read-only: Mintsoft's totals + per-location quantities for a scanned barcode / typed SKU. `refresh` skips the short-lived cache. */
  stockLookup: (q: string, refresh = false) => request<StockLookupResult>(`/api/stock-lookup?q=${encodeURIComponent(q)}${refresh ? '&refresh=1' : ''}`),

  listStockCounts: (status: StockCount['status'] = 'PENDING') =>
    request<StockCount[]>(`/api/stock-counts?status=${encodeURIComponent(status)}`),

  /** Supervisor/Admin only server-side (stockCountAuthorization.ts) — a single id applies just that one count, several apply them together in one Mintsoft call. */
  applyStockCounts: (ids: string[]) =>
    request<ApplyStockCountResult[]>('/api/stock-counts/apply', { method: 'POST', body: JSON.stringify({ ids }) }),

  /** Pure local bookkeeping, no Mintsoft call (cancelStockCount.ts) — withdraws a wrongly-entered PENDING count. Added 2026-10-05 per Suri's "no edit option" feedback. */
  cancelStockCount: (id: string) => request<StockCount>(`/api/stock-counts/${encodeURIComponent(id)}/cancel`, { method: 'POST' }),

  /** Phone picking (2026-10-09). */
  pickBatches: () => request<{ batches: PickBatchSummary[]; me: string }>('/api/picking/batches'),
  pickBatch: (runId: string, batchId: string) => request<BatchPickView>(`/api/picking/batches/${encodeURIComponent(runId)}/${encodeURIComponent(batchId)}`),
  pickConfirm: (input: { runId: string; batchId: string; orderId: number; lineIdx: number; scanned: string; manual: boolean }) =>
    request<PickConfirmResult>('/api/picking/confirm', { method: 'POST', body: JSON.stringify(input) }),
  pickSkip: (input: { runId: string; batchId: string; orderId: number; lineIdx: number; reason: string; note: string; final: boolean }) =>
    request<PickConfirmResult>('/api/picking/skip', { method: 'POST', body: JSON.stringify(input) }),
  pickLabel: (input: { runId: string; batchId: string; orderId: number; scanned: string; manual: boolean }) =>
    request<PickConfirmResult>('/api/picking/label', { method: 'POST', body: JSON.stringify(input) }),
  pickStock: (sku: string, location: string) => request<{ stock: PickStock | null }>(`/api/picking/stock?sku=${encodeURIComponent(sku)}&location=${encodeURIComponent(location)}`),
  /** Replenishment (2026-10-15): the list of pick shelves to top up, and one move posted to Mintsoft. */
  replenList: () => request<ReplenList>('/api/replen'),
  replenMove: (input: { moveId: string; sku: string; from: string; to: string; quantity: number }) =>
    request<ReplenMoveResult>('/api/replen/move', { method: 'POST', body: JSON.stringify(input) }),
  /** Batch release (2026-10-16): only for logins ticked "Release batches" on the admin Operators page. */
  releaseList: () => request<ReleaseList>('/api/release/batches'),
  releaseAssign: (input: { runId: string; batchId: string; userId: string }) =>
    request<{ ok: boolean; message: string }>('/api/release/assign', { method: 'POST', body: JSON.stringify(input) }),
  releaseReopenShort: (input: { batchId: string; orderId: number; orderNumber: string }) =>
    request<{ ok: boolean; message: string }>('/api/release/reopen-short', { method: 'POST', body: JSON.stringify(input) }),
  releaseMarkPicked: (input: { runId: string; batchId: string; creditUserId: string; reason: string }) =>
    request<{ ok: boolean; message: string }>('/api/release/mark-picked', { method: 'POST', body: JSON.stringify(input) }),
  pickUndo: (input: { runId: string; batchId: string }) => request<PickConfirmResult>('/api/picking/undo', { method: 'POST', body: JSON.stringify(input) }),
};
